import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { DeleteOutline } from '@mui/icons-material'

import { BANK_CONNECTION_STATUS } from '@/utils/constants'
import { apiErrorMessage } from '@/utils/apiError'
import { useDeleteConnection } from './useDeleteConnection'

const DELETABLE_STATUSES = [BANK_CONNECTION_STATUS.ACTIVE, BANK_CONNECTION_STATUS.NEAR_EXPIRY]

type Props = {
  userId: string
  connectionId: string
  connectionName: string
  connectionStatus?: string
}

export const DeleteConnection: React.FC<Props> = ({
  userId,
  connectionId,
  connectionName,
  connectionStatus,
}) => {
  const [open, setOpen] = useState(false)
  const { mutate, isPending: isDeleting, error, reset } = useDeleteConnection(userId)
  // Only a live connection -- active, or near expiry but not yet lapsed -- can be deleted; on any
  // other the button is not offered at all. Underscores read as hyphens, as either may be sent.
  const status = connectionStatus?.trim().toLowerCase().replace(/_/g, '-')
  const isDeletable = DELETABLE_STATUSES.some(deletable => deletable === status)

  const handleOpen = () => {
    reset()
    setOpen(true)
  }

  // Closes on success. The success also reloads the list, which takes the deleted card (and this
  // dialog with it) off the page, so nothing else is left to tidy up here.
  const handleConfirm = () => {
    mutate(connectionId, { onSuccess: () => setOpen(false) })
  }

  if (!isDeletable) return null

  return (
    <>
      <Button
        color="error"
        size="small"
        startIcon={isDeleting ? <CircularProgress size={14} color="inherit" /> : <DeleteOutline />}
        onClick={handleOpen}
        disabled={isDeleting}
        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
      >
        {isDeleting ? 'Deleting…' : 'Delete connection'}
      </Button>

      <Dialog
        open={open}
        // Closing mid-delete would hide the outcome, so the dialog stays until the request settles.
        onClose={isDeleting ? undefined : () => setOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby={`delete-connection-title-${connectionId}`}
        sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
      >
        <DialogTitle
          id={`delete-connection-title-${connectionId}`}
          sx={{ fontSize: '18px', fontWeight: 600 }}
        >
          Delete connection
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ fontSize: '14px' }}>
            Delete the <strong>{connectionName}</strong> connection? Its financial accounts will be
            removed from this customer.
          </Typography>
          {isDeleting && (
            <Box display="flex" alignItems="center" gap={1} mt={1.5} role="status">
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                Deleting the connection and reloading accounts…
              </Typography>
            </Box>
          )}
          {!!error && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              The delete failed: {apiErrorMessage(error)}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {isDeleting ? 'Deleting…' : error ? 'Try again' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
